import { useState, useCallback, useRef } from 'react';
import { NewsItem, NewsImpactEntry, Holding, WatchlistItem, MacroState } from '../types';
import { auth } from '../firebase';

export interface NewsAnalysisState {
  isAnalyzing: boolean;
  analysisText: string;
  impactMap: NewsImpactEntry[];
  error: string | null;
}

interface UseNewsDataReturn {
  news: NewsItem[];
  isLoadingFeed: boolean;
  feedError: string | null;
  fetchNewsFeed: (market: 'US' | 'KR' | 'ALL', language?: string, forceRefresh?: boolean) => Promise<void>;
  cachedAt: string | null;

  selectedNews: NewsItem | null;
  selectNews: (item: NewsItem | null) => void;

  analyses: Record<string, NewsAnalysisState>;
  analyzeNews: (
    news: NewsItem,
    holdings: Holding[],
    watchlist: WatchlistItem[],
    macro: MacroState | null,
    market: 'US' | 'KR' | 'ALL',
    language: string
  ) => Promise<void>;
  autoAnalyzeNewsList: (
    items: NewsItem[],
    holdings: Holding[],
    watchlist: WatchlistItem[],
    macro: MacroState | null,
    market: 'US' | 'KR' | 'ALL',
    language: string
  ) => void;
  clearAnalysis: (newsId?: string) => void;
}

async function getSafeToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

function repairIncompleteJsonArray(jsonStr: string): string {
  let trimmed = jsonStr.trim();
  if (!trimmed.startsWith('[')) {
    const startBracket = trimmed.indexOf('[');
    if (startBracket !== -1) {
      trimmed = trimmed.slice(startBracket);
    } else {
      return '[]';
    }
  }

  try {
    JSON.parse(trimmed);
    return trimmed;
  } catch (e) {
    // Continue repairing
  }

  const lastOpenBrace = trimmed.lastIndexOf('{');
  const lastCloseBrace = trimmed.lastIndexOf('}');

  if (lastOpenBrace > lastCloseBrace) {
    if (lastCloseBrace !== -1) {
      trimmed = trimmed.slice(0, lastCloseBrace + 1).trim();
    } else {
      return '[]';
    }
  }

  if (trimmed.endsWith(',')) {
    trimmed = trimmed.slice(0, -1).trim();
  }

  if (!trimmed.endsWith(']')) {
    trimmed += ']';
  }

  try {
    JSON.parse(trimmed);
    return trimmed;
  } catch (e) {
    const objects: string[] = [];
    const regex = /\{[\s\S]*?\}/g;
    let match;
    while ((match = regex.exec(jsonStr)) !== null) {
      try {
        JSON.parse(match[0]);
        objects.push(match[0]);
      } catch (err) {}
    }
    return '[' + objects.join(',') + ']';
  }
}

function parseImpactMap(text: string, holdings: Holding[], watchlist: WatchlistItem[]): NewsImpactEntry[] {
  try {
    const startIdx = text.indexOf('[IMPACT_MAP]');
    const endIdx = text.indexOf('[POSITION_STRATEGY]');
    if (startIdx === -1) return [];

    const section = endIdx !== -1
      ? text.slice(startIdx + '[IMPACT_MAP]'.length, endIdx)
      : text.slice(startIdx + '[IMPACT_MAP]'.length);

    const arrayStart = section.indexOf('[');
    if (arrayStart === -1) return [];

    const jsonSubStr = section.slice(arrayStart);
    const repairedJson = repairIncompleteJsonArray(jsonSubStr);

    const raw: any[] = JSON.parse(repairedJson);
    const holdingSymbols = new Set(holdings.map(h => h.symbol.toUpperCase()));
    const watchlistSymbols = new Set(watchlist.map(w => w.symbol.toUpperCase()));

    return raw.map(item => ({
      symbol: item.symbol || '',
      name: item.name,
      direction: (['bullish', 'bearish', 'neutral'].includes(item.direction) ? item.direction : 'neutral') as any,
      magnitude: Math.max(-100, Math.min(100, Number(item.magnitude) || 0)),
      rationale: item.rationale || '',
      recommendedAction: (['BUY', 'SELL', 'HOLD', 'WATCH', 'REDUCE'].includes(item.recommendedAction)
        ? item.recommendedAction
        : 'HOLD') as any,
      isHolding: holdingSymbols.has((item.symbol || '').toUpperCase()),
      isWatchlist: watchlistSymbols.has((item.symbol || '').toUpperCase()),
    }));
  } catch (e) {
    console.error('[useNewsData] parseImpactMap error:', e);
    return [];
  }
}

export function useNewsData(): UseNewsDataReturn {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoadingFeed, setIsLoadingFeed] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [analyses, setAnalyses] = useState<Record<string, NewsAnalysisState>>({});

  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const isAutoQueueRunningRef = useRef(false);

  const fetchNewsFeed = useCallback(async (
    market: 'US' | 'KR' | 'ALL',
    language: string = 'ko',
    forceRefresh = false
  ) => {
    setIsLoadingFeed(true);
    setFeedError(null);
    try {
      const markets = market === 'ALL' ? ['US', 'KR'] : [market];
      const token = await getSafeToken();

      const results = await Promise.all(markets.map(async m => {
        const url = `/api/news/feed?market=${m}&lang=${language}${forceRefresh ? '&refresh=true' : ''}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`News feed failed: ${res.status}`);
        return res.json() as Promise<{ items: NewsItem[]; cachedAt: string }>;
      }));

      const combined = results.flatMap(r => r.items);
      combined.sort((a, b) => {
        return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
      });

      setNews(combined.slice(0, 40));
      setCachedAt(results[0]?.cachedAt ?? null);
    } catch (err: any) {
      setFeedError(err.message);
    } finally {
      setIsLoadingFeed(false);
    }
  }, []);

  const analyzeNews = useCallback(async (
    newsItem: NewsItem,
    holdings: Holding[],
    watchlist: WatchlistItem[],
    macro: MacroState | null,
    market: 'US' | 'KR' | 'ALL',
    language: string
  ) => {
    const newsId = newsItem.id;

    // Abort ongoing analysis for this specific news if exists
    if (abortControllersRef.current[newsId]) {
      abortControllersRef.current[newsId].abort();
    }
    abortControllersRef.current[newsId] = new AbortController();

    setAnalyses(prev => ({
      ...prev,
      [newsId]: {
        isAnalyzing: true,
        analysisText: '',
        impactMap: [],
        error: null
      }
    }));

    const token = await getSafeToken();

    try {
      const res = await fetch('/api/ai/news-impact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        signal: abortControllersRef.current[newsId].signal,
        body: JSON.stringify({
          newsId,
          newsTitle: newsItem.title,
          newsContent: newsItem.content,
          newsUrl: newsItem.url,
          holdings: holdings.map(h => ({
            symbol: h.symbol,
            name: h.name,
            weight: h.weight,
            market: h.market,
          })),
          watchlist: watchlist.map(w => ({
            symbol: w.symbol,
            name: w.name,
            market: w.market,
          })),
          macro: macro ? {
            vix: (macro as any).vix?.value ?? null,
            sp500Change: (macro as any).sp500?.change ?? null,
            kospiChange: (macro as any).kospi?.change ?? null,
            regime: macro.marketRegime ?? macro.regimes?.policy ?? null,
            fedFunds: macro.fedFunds?.value ?? null,
          } : {},
          language: language === 'ko' ? 'ko' : 'en',
          market,
        }),
      });

      if (!res.ok || !res.body) throw new Error('Analysis request failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

// eslint-disable-next-line
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const raw = trimmed.slice(5).trim();
          if (raw === '[DONE]') break;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.text) {
              accumulated += parsed.text;
              
              setAnalyses(prev => {
                const current = prev[newsId];
                if (!current) return prev;
                
                // Parse impact map once we see the position strategy section starting
                let parsedMap = current.impactMap;
                if (accumulated.includes('[POSITION_STRATEGY]') && parsedMap.length === 0) {
                  parsedMap = parseImpactMap(accumulated, holdings, watchlist);
                }

                return {
                  ...prev,
                  [newsId]: {
                    ...current,
                    analysisText: accumulated,
                    impactMap: parsedMap
                  }
                };
              });
            }
          } catch (e: any) {
            if (e.message && (e.message.includes('API Error') || e.message.includes('크레딧') || e.message.includes('API key'))) {
              throw e;
            }
          }
        }
      }

      // Final Map Parse
      const finalParsed = parseImpactMap(accumulated, holdings, watchlist);
      setAnalyses(prev => ({
        ...prev,
        [newsId]: {
          isAnalyzing: false,
          analysisText: accumulated,
          impactMap: finalParsed,
          error: null
        }
      }));

    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error(`[useNewsData] Analysis error for ${newsId}:`, err);
        const isFriendlyError = err.message.includes('API Error') || err.message.includes('크레딧') || err.message.includes('API key');
        const errText = isFriendlyError ? `⚠️ ${err.message}` : (language === 'ko'
          ? '⚠️ 분석 중 오류가 발생했습니다.'
          : '⚠️ Analysis failed.');
        
        setAnalyses(prev => ({
          ...prev,
          [newsId]: {
            isAnalyzing: false,
            analysisText: errText,
            impactMap: [],
            error: err.message
          }
        }));
      }
    }
  }, []);

  // Auto Analysis for top 5 news items sequentially
  const autoAnalyzeNewsList = useCallback((
    items: NewsItem[],
    holdings: Holding[],
    watchlist: WatchlistItem[],
    macro: MacroState | null,
    market: 'US' | 'KR' | 'ALL',
    language: string
  ) => {
    if (isAutoQueueRunningRef.current || items.length === 0) return;
    isAutoQueueRunningRef.current = true;

    // We auto-analyze the top 5 articles sequentially to save Gemini API rate limits
    const targetItems = items.slice(0, 5);

    const runQueue = async () => {
      for (const item of targetItems) {
        // Skip if already analyzed or currently analyzing
        if (analyses[item.id]?.analysisText || analyses[item.id]?.isAnalyzing) {
          continue;
        }
        await analyzeNews(item, holdings, watchlist, macro, market, language);
      }
      isAutoQueueRunningRef.current = false;
    };

    runQueue();
  }, [analyses, analyzeNews]);

  const clearAnalysis = useCallback((newsId?: string) => {
    if (newsId) {
      if (abortControllersRef.current[newsId]) {
        abortControllersRef.current[newsId].abort();
        delete abortControllersRef.current[newsId];
      }
      setAnalyses(prev => {
        const next = { ...prev };
        delete next[newsId];
        return next;
      });
    } else {
      // Clear all abort controllers
      Object.values(abortControllersRef.current).forEach(c => c.abort());
      abortControllersRef.current = {};
      setAnalyses({});
    }
  }, []);

  return {
    news,
    isLoadingFeed,
    feedError,
    fetchNewsFeed,
    cachedAt,
    selectedNews,
    selectNews: setSelectedNews,
    analyses,
    analyzeNews,
    autoAnalyzeNewsList,
    clearAnalysis,
  };
}
